
import { useState, lazy, Suspense } from 'react';
import { Vehicle, VehicleFormData } from '../types';
import { VehicleCard } from './VehicleCard';
import { ImportAds } from './ImportAds';
import { AdminSettings } from './AdminSettings';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, Settings, Cog, FileText, Trash2, Download } from 'lucide-react';
import { generateBlankVehiclePDF } from '@/utils/generateBlankVehiclePDF';
import { generateCatalogPDF } from '@/utils/generateCatalogPDF';
import { useDeleteImportedVehicles } from '@/hooks/useVehicles';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

// Lazy load VehicleForm since it's only needed when editing/creating
const VehicleForm = lazy(() => import('./VehicleForm').then(module => ({ default: module.VehicleForm })));

interface AdminPanelProps {
  vehicles: Vehicle[];
  onAddVehicle: (data: VehicleFormData) => void;
  onUpdateVehicle: (id: string, data: VehicleFormData) => void;
  onDeleteVehicle: (id: string) => void;
  isLoading?: boolean;
}

export const AdminPanel = ({
  vehicles,
  onAddVehicle,
  onUpdateVehicle,
  onDeleteVehicle,
  isLoading = false
}: AdminPanelProps) => {
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | undefined>();
  const { isFeatureEnabled } = useFeatureFlags();

  const deleteImportedMutation = useDeleteImportedVehicles();
  const { toast } = useToast();

  const importedCount = vehicles.filter(v => (v as any).sourceUrl || (v as any).source_url).length;

  const handleDeleteImported = async () => {
    try {
      const count = await deleteImportedMutation.mutateAsync();
      toast({
        title: "Nettoyage terminé",
        description: `${count} véhicule(s) importé(s) supprimé(s).`,
      });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer les véhicules importés.",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = (data: VehicleFormData) => {
    if (editingVehicle) {
      onUpdateVehicle(editingVehicle.id, data);
    } else {
      onAddVehicle(data);
    }
    setShowForm(false);
    setEditingVehicle(undefined);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingVehicle(undefined);
  };

  const handleImportComplete = () => {
    // Trigger a refresh of the vehicles list
    window.location.reload();
  };

  if (showSettings) {
    return (
      <AdminSettings onBack={() => setShowSettings(false)} />
    );
  }

  if (showForm) {
    return (
      <Suspense fallback={
        <div className="flex justify-center items-center py-12">
          <div className="flex items-center space-x-3 text-gray-600">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Chargement du formulaire...</span>
          </div>
        </div>
      }>
        <VehicleForm
          vehicle={editingVehicle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Suspense>
    );
  }

  return (
    <div className="space-y-8">
      {/* En-tête moderne */}
      <Card className="border-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Settings className="h-6 w-6" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Panneau d'administration</CardTitle>
                <p className="text-blue-100 mt-1">
                  {isLoading ? 'Chargement...' : `${vehicles.length} annonce(s) publiée(s)`}
                </p>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button 
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                size="lg"
              >
                <Cog className="h-5 w-5 mr-2" />
                Paramètres
              </Button>
              <Button 
                onClick={() => setShowForm(true)} 
                className="bg-white text-blue-600 hover:bg-blue-50 font-medium"
                size="lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Nouvelle annonce
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Section d'importation */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-1">
          <ImportAds onImportComplete={handleImportComplete} />
        </div>
        <div className="md:col-span-2">
          <Card className="h-full">
            <CardHeader>
              <CardTitle className="text-lg">Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{vehicles.length}</div>
                  <div className="text-sm text-gray-600">Annonces actives</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">
                    {vehicles.filter(v => v.price > 0).length}
                  </div>
                  <div className="text-sm text-gray-600">Avec prix affiché</div>
                </div>
              </div>
              
              {/* Bouton fiche vierge PDF */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => generateBlankVehiclePDF()}
              >
                <FileText className="h-4 w-4 mr-2" />
                Imprimer fiche vierge
              </Button>
              
              {/* Bouton export catalogue PDF */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => { generateCatalogPDF(vehicles); }}
                disabled={vehicles.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Exporter le catalogue PDF
              </Button>
              
              {/* Bouton suppression véhicules importés */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    disabled={deleteImportedMutation.isPending}
                  >
                    {deleteImportedMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Supprimer les véhicules importés
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer tous les véhicules importés ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action supprimera tous les véhicules ayant une URL source (importés automatiquement). 
                      Les véhicules créés manuellement ne seront pas affectés. Cette action est irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteImported} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Supprimer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              
              {/* Actions supplémentaires basées sur les feature flags */}
              <div className="space-y-3">
                {isFeatureEnabled('bulkOperations') && (
                  <Button variant="outline" className="w-full">
                    Opérations en lot
                  </Button>
                )}
                {isFeatureEnabled('exportFeatures') && (
                  <Button variant="outline" className="w-full">
                    Exporter les données
                  </Button>
                )}
                {isFeatureEnabled('analytics') && (
                  <Button variant="outline" className="w-full">
                    Voir les statistiques
                  </Button>
                )}
              </div>
              
              <p className="text-sm text-gray-600">
                Utilisez l'importation automatique pour récupérer les annonces depuis vos sites partenaires,
                ou créez manuellement de nouvelles annonces.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Contenu */}
      {isLoading ? (
        <Card>
          <CardContent className="flex justify-center items-center py-12">
            <div className="flex items-center space-x-3 text-gray-600">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Chargement des annonces...</span>
            </div>
          </CardContent>
        </Card>
      ) : vehicles.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune annonce</h3>
              <p className="text-gray-600 mb-6">
                Commencez par importer automatiquement les annonces ou créez votre première annonce manuellement.
              </p>
              <div className="space-y-3">
                <Button onClick={() => setShowForm(true)} size="lg">
                  <Plus className="h-5 w-5 mr-2" />
                  Créer une annonce
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              isAdmin={true}
              onEdit={handleEdit}
              onDelete={onDeleteVehicle}
            />
          ))}
        </div>
      )}
    </div>
  );
};
